import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { messagingService } from '../../services/api/messagingService';
import { useAuth } from '../../context/AuthContext';
import SEO from '../common/SEO/SEO';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import {
  ContactPanel,
  EmbeddedFormCard,
  Eyebrow,
  FieldError,
  FieldInput,
  FieldLabel,
  FieldRow,
  FieldTextarea,
  Form,
  FormCard,
  FormFootnote,
  Heading,
  HomeLink,
  InfoIcon,
  InfoList,
  Intro,
  Lead,
  SignedInNote,
  SubmitButton,
  SubmitError,
  SuccessBox,
  SuccessIcon,
  SuccessText,
  SuccessTitle,
} from './ContactUsForm.styled';
import { toPersonNameCase } from '../../utils/normalizePersonName';

function buildContactIdentityFromUser(user) {
  const email = String(user?.Email || '').trim();
  if (!email) return null;
  const first = String(user?.FirstName || '').trim();
  const last = String(user?.LastName || '').trim();
  return {
    email,
    firstName: first || 'User',
    lastName: last || '(not provided)',
  };
}

const CONTACT_SEO = {
  title: 'Contact',
  description:
    'Contact the NKY Connect team with questions, feedback, or support requests. We typically reply within 1–2 business days.',
  path: '/contact',
};

/** `embedded` renders just the form card (launch page); otherwise the full `/contact` page. */
const ContactUsForm = ({ embedded = false }) => {
  const { user, loading: authLoading } = useAuth();
  const useAccountForContact = !authLoading && Boolean(buildContactIdentityFromUser(user));

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleNameBlur = (e) => {
    const { name, value } = e.target;
    if (name !== 'firstName' && name !== 'lastName') return;
    const normalized = toPersonNameCase(value);
    if (normalized === value) return;
    setFormData((prev) => ({ ...prev, [name]: normalized }));
  };

  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const validateForm = () => {
    const newErrors = {};
    if (!useAccountForContact) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required.';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required.';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required.';
      } else if (!isValidEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address.';
      }
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitError('');
    setIsLoading(true);

    try {
      let firstName;
      let lastName;
      let email;
      let messageBody = formData.message.trim();

      if (useAccountForContact) {
        const id = buildContactIdentityFromUser(user);
        firstName = id.firstName;
        lastName = id.lastName;
        email = id.email;
        const display = [user.FirstName, user.LastName].filter(Boolean).join(' ').trim();
        const who = display ? `${display} <${email}>` : email;
        messageBody = `[Signed-in account: ${who}]\n\n${messageBody}`;
      } else {
        firstName = toPersonNameCase(formData.firstName);
        lastName = toPersonNameCase(formData.lastName);
        email = formData.email.trim();
      }

      await messagingService.submitContactForm({
        firstName,
        lastName,
        email,
        message: messageBody,
      });

      setFormData({ firstName: '', lastName: '', email: '', message: '' });
      setErrors({});
      setSubmitted(true);
    } catch (error) {
      console.error('API Call Failed:', error);
      setSubmitError('There was an issue sending your message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fieldProps = (name) => ({
    id: `contact-${name}`,
    name,
    value: formData[name],
    onChange: handleChange,
    'aria-invalid': Boolean(errors[name]),
    'aria-describedby': errors[name] ? `contact-${name}-error` : undefined,
  });

  const fieldError = (name) =>
    errors[name] ? <FieldError id={`contact-${name}-error`}>{errors[name]}</FieldError> : null;

  const content = submitted ? (
    <SuccessBox role="status">
      <SuccessIcon aria-hidden>
        <CheckRoundedIcon />
      </SuccessIcon>
      <SuccessTitle>Message sent</SuccessTitle>
      <SuccessText>
        Thanks, we received your message and emailed our team. We typically reply within 1–2
        business days.
      </SuccessText>
      <HomeLink as={RouterLink} to="/">
        Back to home
      </HomeLink>
    </SuccessBox>
  ) : (
    <Form onSubmit={handleSubmit} noValidate>
      {useAccountForContact && (
        <SignedInNote>
          You&apos;re signed in, so we&apos;ll include your account name and email with this
          message so our team can reply.
        </SignedInNote>
      )}

      {!useAccountForContact && (
        <>
          <FieldRow>
            <FieldLabel>
              First name
              <FieldInput
                {...fieldProps('firstName')}
                placeholder="Jane"
                onBlur={handleNameBlur}
                autoComplete="given-name"
              />
              {fieldError('firstName')}
            </FieldLabel>
            <FieldLabel>
              Last name
              <FieldInput
                {...fieldProps('lastName')}
                placeholder="Smith"
                onBlur={handleNameBlur}
                autoComplete="family-name"
              />
              {fieldError('lastName')}
            </FieldLabel>
          </FieldRow>

          <FieldLabel>
            Email
            <FieldInput
              {...fieldProps('email')}
              type="email"
              placeholder="jane.smith@email.com"
              autoComplete="email"
            />
            {fieldError('email')}
          </FieldLabel>
        </>
      )}

      <FieldLabel>
        Message
        <FieldTextarea
          {...fieldProps('message')}
          placeholder="Tell us what's on your mind. The more detail, the better we can help."
        />
        {fieldError('message')}
      </FieldLabel>

      {submitError && <SubmitError role="alert">{submitError}</SubmitError>}

      <SubmitButton type="submit" disabled={isLoading} $compact={embedded}>
        {isLoading ? <NkySpinner size="sm" onDark /> : 'Send message'}
      </SubmitButton>
      <FormFootnote>We respect your privacy and will never share your information.</FormFootnote>
    </Form>
  );

  if (embedded) {
    return <EmbeddedFormCard>{content}</EmbeddedFormCard>;
  }

  return (
    <>
      <SEO {...CONTACT_SEO} />
      <ContactPanel aria-labelledby="contact-title">
        <Intro>
          <Eyebrow>Contact us</Eyebrow>
          <Heading id="contact-title">How can we help?</Heading>
          <Lead>
            Whether you have a question, feedback, or need support, we&apos;re here for you.
          </Lead>
          <InfoList>
            <li>
              <InfoIcon aria-hidden>
                <ScheduleRoundedIcon />
              </InfoIcon>
              <span>
                <strong>Replies in 1–2 business days</strong>
                Sent straight to the NKY Connect team
              </span>
            </li>
            <li>
              <InfoIcon aria-hidden>
                <ForumRoundedIcon />
              </InfoIcon>
              <span>
                <strong>Homeowners and pros</strong>
                Questions about a request, a listing, or your account
              </span>
            </li>
            <li>
              <InfoIcon aria-hidden>
                <LockRoundedIcon />
              </InfoIcon>
              <span>
                <strong>Your info stays private</strong>
                We only use it to reply to you
              </span>
            </li>
          </InfoList>
        </Intro>
        <FormCard>{content}</FormCard>
      </ContactPanel>
    </>
  );
};

export default ContactUsForm;
